<template>
    <div
        ref="root"
        class="security-code-pad"
        :class="{'security-code-pad--disabled': disabled}"
        tabindex="0"
        @click="focusInput"
        @keydown="handleKeydown">
        <input
            ref="input"
            class="security-code-pad__input"
            :value="value"
            :maxlength="length"
            :disabled="disabled"
            readonly
            tabindex="-1"
            aria-hidden="true"
            inputmode="numeric"
            autocomplete="one-time-code"
            @input="updateValue($event.target.value)">
        <div class="security-code-pad__slots" :style="slotStyle">
            <button
                v-for="index in length"
                :key="'slot-' + index"
                type="button"
                class="security-code-pad__slot"
                :class="{
                    'security-code-pad__slot--filled': digitAt(index),
                    'security-code-pad__slot--active': isActive(index),
                    'security-code-pad__slot--error': firstError
                }"
                :disabled="disabled"
                @click.stop="focusInput">
                <span v-if="digitAt(index)">{{ masked ? '•' : digitAt(index) }}</span>
            </button>
        </div>
        <div v-if="firstError" class="error--text caption text-center mt-2">{{ firstError }}</div>
        <div class="security-code-pad__keyboard mt-3">
            <v-btn
                v-for="key in digitKeys"
                :key="'key-' + key"
                type="button"
                class="security-code-pad__key"
                color="secondary"
                depressed
                :disabled="disabled"
                @click.stop="appendDigit(key)">
                {{ key }}
            </v-btn>
            <v-btn
                type="button"
                class="security-code-pad__key"
                color="secondary"
                depressed
                :disabled="disabled || !normalizedValue"
                @click.stop="clearValue">
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-btn
                type="button"
                class="security-code-pad__key"
                color="secondary"
                depressed
                :disabled="disabled"
                @click.stop="appendDigit('0')">
                0
            </v-btn>
            <v-btn
                type="button"
                class="security-code-pad__key"
                color="secondary"
                depressed
                :disabled="disabled || !normalizedValue"
                @click.stop="removeDigit">
                <v-icon>mdi-backspace-outline</v-icon>
            </v-btn>
        </div>
    </div>
</template>

<script>
export default {
    name: 'SecurityCodePad',
    data: () => ({
        lastCompleteValue: ''
    }),
    props: {
        value: {
            type: [String, Number],
            default: ''
        },
        length: {
            type: Number,
            default: 6
        },
        disabled: {
            type: Boolean,
            default: false
        },
        autofocus: {
            type: Boolean,
            default: false
        },
        masked: {
            type: Boolean,
            default: true
        },
        errorMessages: {
            type: [Array, String],
            default: () => []
        }
    },
    computed: {
        normalizedValue() {
            return this.normalize(this.value);
        },
        digitKeys() {
            return ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
        },
        firstError() {
            if (Array.isArray(this.errorMessages)) {
                return this.errorMessages.length ? this.errorMessages[0] : '';
            }

            return this.errorMessages || '';
        },
        slotStyle() {
            return {
                gridTemplateColumns: 'repeat(' + this.length + ', minmax(28px, 1fr))'
            };
        }
    },
    mounted() {
        if (this.autofocus) {
            this.focusInput();
        }
    },
    watch: {
        value(value) {
            if (this.normalize(value).length < this.length) {
                this.lastCompleteValue = '';
            }
        }
    },
    methods: {
        normalize(value) {
            return String(value || '').replace(/\D/g, '').slice(0, this.length);
        },
        digitAt(index) {
            return this.normalizedValue.charAt(index - 1);
        },
        isActive(index) {
            return !this.disabled
                && !this.firstError
                && this.normalizedValue.length < this.length
                && index === this.normalizedValue.length + 1;
        },
        focusInput() {
            if (this.disabled) return;

            this.$nextTick(() => {
                if (this.$refs.root && typeof this.$refs.root.focus === 'function') {
                    try {
                        this.$refs.root.focus({preventScroll: true});
                    } catch (error) {
                        this.$refs.root.focus();
                    }
                }
            });
        },
        updateValue(value) {
            if (this.disabled) return;

            const normalized = this.normalize(value);
            this.$emit('input', normalized);
            if (normalized.length < this.length) {
                this.lastCompleteValue = '';
            }

            if (normalized.length === this.length && normalized !== this.lastCompleteValue) {
                this.lastCompleteValue = normalized;
                this.$emit('complete', normalized);
            }
        },
        appendDigit(digit) {
            if (this.disabled || this.normalizedValue.length >= this.length) return;

            this.updateValue(this.normalizedValue + digit);
            this.focusInput();
        },
        removeDigit() {
            if (this.disabled || !this.normalizedValue) return;

            this.updateValue(this.normalizedValue.slice(0, -1));
            this.focusInput();
        },
        clearValue() {
            if (this.disabled) return;

            this.lastCompleteValue = '';
            this.updateValue('');
            this.focusInput();
        },
        handleKeydown(event) {
            if (this.disabled) return;

            if (/^\d$/.test(event.key)) {
                event.preventDefault();
                this.appendDigit(event.key);
            } else if (event.key === 'Backspace') {
                event.preventDefault();
                this.removeDigit();
            } else if (event.key === 'Delete' || event.key === 'Escape') {
                event.preventDefault();
                this.clearValue();
            } else if (event.key === 'Enter') {
                event.preventDefault();
                this.$emit('submit');
            }
        }
    }
}
</script>

<style scoped>
.security-code-pad {
    outline: none;
    margin: 0 auto;
    max-width: 360px;
    touch-action: manipulation;
    user-select: none;
    width: 100%;
}

.security-code-pad__input {
    height: 1px;
    opacity: 0;
    position: absolute;
    width: 1px;
}

.security-code-pad__slots {
    display: grid;
    gap: 12px;
    margin: 0 auto;
    width: 100%;
}

.security-code-pad__slot {
    align-items: center;
    background: transparent;
    border: 0;
    border-bottom: 2px solid currentColor;
    color: var(--v-primary-base);
    display: flex;
    font-size: 28px;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    height: 48px;
    justify-content: center;
    line-height: 1;
    opacity: .52;
    padding: 0;
}

.security-code-pad__slot--filled,
.security-code-pad__slot--active {
    opacity: 1;
}

.security-code-pad__slot--active {
    border-bottom-width: 3px;
}

.security-code-pad__slot--error {
    color: var(--v-error-base);
    opacity: 1;
}

.security-code-pad__keyboard {
    display: grid;
    gap: 12px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin-left: auto;
    margin-right: auto;
    max-width: 320px;
    width: 100%;
}

.security-code-pad__key.v-btn {
    border-radius: 8px;
    color: var(--v-primary-base);
    font-size: 24px;
    font-weight: 700;
    height: 56px;
    min-width: 0;
}

.security-code-pad--disabled {
    opacity: .72;
}

@media (max-width: 600px) {
    .security-code-pad {
        max-width: 340px;
    }

    .security-code-pad__slots {
        gap: 14px;
    }

    .security-code-pad__slot {
        font-size: 30px;
        height: 54px;
    }

    .security-code-pad__keyboard {
        gap: 12px;
        max-width: 340px;
    }

    .security-code-pad__key.v-btn {
        font-size: 26px;
        height: 64px;
    }
}
</style>
